import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineHome, HiOutlineUsers, HiOutlineX, HiOutlineLogout, HiOutlineSearch } from 'react-icons/hi';
import { useAuth } from '../../context/AuthContext';
import AppSetupCard from './AppSetupCard';
import './Sidebar.css';

/**
 * Left navigation for the home page.
 *
 * Renders from the same `modules` list the home grid uses — passed in already
 * filtered by card access, so the sidebar can never offer a module the user
 * would be refused on the server.
 *
 * On mobile it becomes an off-canvas drawer driven by `open`/`onClose`; the
 * backdrop is rendered here so the caller only owns the flag.
 *
 * The search box filters what is already on screen — it is not a lookup. It can
 * therefore never surface a module the user lacks the card for, because `modules`
 * arrives pre-filtered by card access. Home and User Management are matched too,
 * so a search that finds nothing really means nothing is there.
 */
const Sidebar = ({ modules, badges = {}, activeId = 'home', isAdmin, open = false, onClose }) => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [query, setQuery] = useState('');

    const q = query.trim().toLowerCase();
    const matches = (...labels) => !q || labels.some(l => (l || '').toLowerCase().includes(q));

    const visibleModules = useMemo(
        () => modules.filter(m => matches(m.title, m.shortTitle, m.description)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [modules, q]
    );

    const showHome = matches('Home');
    const showUserManagement = isAdmin && matches('User Management');
    const nothingFound = !!q && !showHome && !showUserManagement && visibleModules.length === 0;

    const go = (path) => {
        onClose?.();
        navigate(path);
    };

    const handleLogout = async () => {
        onClose?.();
        await logout();
        navigate('/login');
    };

    return (
        <>
            <div
                className={`app-sidebar-backdrop ${open ? 'is-open' : ''}`}
                onClick={onClose}
                aria-hidden="true"
            />

            <aside className={`app-sidebar ${open ? 'is-open' : ''}`}>
                <div className="app-sidebar-brand">
                    <span className="app-sidebar-logo-tile">
                        <img src="/logo.png" alt="PrimeOffice Manager" />
                    </span>
                    <span className="app-sidebar-brand-name">PrimeOffice Manager</span>
                    <button
                        type="button"
                        className="app-sidebar-close"
                        onClick={onClose}
                        aria-label="Close navigation"
                    >
                        <HiOutlineX size={20} />
                    </button>
                </div>

                <div className="app-sidebar-search">
                    <HiOutlineSearch size={16} className="app-sidebar-search-icon" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search modules"
                        aria-label="Search modules"
                    />
                    {query && (
                        <button
                            type="button"
                            className="app-sidebar-search-clear"
                            onClick={() => setQuery('')}
                            aria-label="Clear search"
                        >
                            <HiOutlineX size={14} />
                        </button>
                    )}
                </div>

                <nav className="app-sidebar-nav">
                    {showHome && (
                        <button
                            type="button"
                            className={`app-sidebar-item ${activeId === 'home' ? 'is-active' : ''}`}
                            onClick={() => go('/purpose-selection')}
                        >
                            <HiOutlineHome size={20} />
                            <span className="app-sidebar-label">Home</span>
                        </button>
                    )}

                    {visibleModules.map(module => {
                        const Icon = module.icon;
                        const badge = badges[module.id];
                        return (
                            <button
                                key={module.id}
                                type="button"
                                className={`app-sidebar-item ${activeId === module.id ? 'is-active' : ''}`}
                                onClick={() => go(module.path)}
                                title={module.title}
                            >
                                {Icon && <Icon size={20} />}
                                <span className="app-sidebar-label">{module.shortTitle || module.title}</span>
                                {badge > 0 && <span className="app-sidebar-badge">{badge}</span>}
                            </button>
                        );
                    })}

                    {nothingFound && (
                        <p className="app-sidebar-empty">No modules match “{query}”.</p>
                    )}

                    {showUserManagement && (
                        <>
                            <div className="app-sidebar-divider" />
                            <button
                                type="button"
                                className="app-sidebar-item"
                                onClick={() => go('/admin/users')}
                            >
                                <HiOutlineUsers size={20} />
                                <span className="app-sidebar-label">User Management</span>
                            </button>
                        </>
                    )}
                </nav>

                <div className="app-sidebar-footer">
                    <AppSetupCard />
                    <button
                        type="button"
                        className="app-sidebar-logout"
                        onClick={handleLogout}
                        title="Logout"
                    >
                        <HiOutlineLogout size={20} />
                        <span className="app-sidebar-label">Logout</span>
                    </button>
                    <span className="app-sidebar-tagline">Smart. Simple. Seamless.</span>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
